import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./CustomSelect.css";

const CustomSelect = ({
  label,
  options = [],
  value,
  onChange,
  placeholder,
  optionLabelKey = "label",
  optionValueKey = "value",
  disabled = false,
  searchable = true,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = (option) => {
    onChange(option);
    setOpen(false);
    setSearch("");
  };

  const selectedOption = useMemo(
    () => options.find((opt) => opt[optionValueKey] === value),
    [options, value, optionValueKey],
  );

  const displayText = useMemo(() => {
    if (!selectedOption) return placeholder;

    const labelVal = selectedOption[optionLabelKey];
    if (!labelVal || typeof labelVal !== "string") return placeholder;

    if (labelVal.toLowerCase().includes("none")) return placeholder;

    return labelVal;
  }, [selectedOption, placeholder, optionLabelKey]);

  const textClassName =
    selectedOption && displayText !== placeholder ? "selected" : "placeholder";

  const filteredOptions = useMemo(() => {
    const list = options.filter((opt) => {
      const labelVal = opt[optionLabelKey];
      if (!labelVal || typeof labelVal !== "string") return true;
      return !labelVal.toLowerCase().includes("none") && opt.type !== "placeholder";
    });

    if (!search.trim()) return list;

    const query = search.trim().toLowerCase();

    // Group items by headers matching mobile FlatList logic:
    // A header is kept if and only if at least one item under it matches!
    const result = [];
    let currentHeader = null;
    let currentHeaderItems = [];

    list.forEach((item) => {
      if (item.type === "header") {
        if (currentHeader && currentHeaderItems.length > 0) {
          result.push(currentHeader, ...currentHeaderItems);
        }
        currentHeader = item;
        currentHeaderItems = [];
      } else {
        const itemLabel = String(item[optionLabelKey] || "").toLowerCase();
        if (itemLabel.includes(query)) {
          if (currentHeader) {
            currentHeaderItems.push(item);
          } else {
            result.push(item);
          }
        }
      }
    });

    if (currentHeader && currentHeaderItems.length > 0) {
      result.push(currentHeader, ...currentHeaderItems);
    }

    return result;
  }, [options, search, optionLabelKey]);

  const nonHeaderCount = options.filter(
    (o) => o.type !== "placeholder" && o.type !== "header",
  ).length;
  const shouldShowSearch = searchable && nonHeaderCount > 3;

  return (
    <div className="custom-select-wrapper" ref={wrapperRef}>
      {label && <label className="custom-select-label">{label}</label>}

      <div
        className={`custom-select-input ${disabled ? "disabled" : ""}`}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => {
              if (prev) setSearch("");
              return !prev;
            });
          }
        }}
      >
        <span className={textClassName}>{displayText}</span>

        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          className={open ? "rotated" : ""}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="#9A99AB"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            className="custom-select-dropdown"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
          >
            {shouldShowSearch && (
              <div
                className="custom-select-search-container"
                onClick={(e) => e.stopPropagation()}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9A99AB"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="custom-select-search-icon"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  className="custom-select-search-input"
                  placeholder={
                    searchPlaceholder || t("search", { defaultValue: "Search" })
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
                {search && (
                  <button
                    type="button"
                    className="custom-select-search-clear"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            <div className="custom-select-options-list">
              {filteredOptions.length === 0 ? (
                <div className="custom-select-no-results">
                  {t("home.noResultsFound", {
                    defaultValue: "No results found",
                  })}
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isTextOnly =
                    opt.type === "header" ||
                    opt.type === "no_services" ||
                    opt.type === "no_listings" ||
                    opt.type === "no_vehicles";

                  // TEXT ONLY HEADER WITH SEPARATION LINES
                  if (isTextOnly) {
                    return (
                      <div
                        key={opt[optionValueKey]}
                        className="custom-select-text"
                      >
                        <span className="custom-select-text-label">
                          {opt[optionLabelKey]}
                        </span>
                      </div>
                    );
                  }

                  const isSelected = value === opt[optionValueKey];

                  return (
                    <div
                      key={opt[optionValueKey]}
                      className={`custom-select-option ${
                        isSelected ? "selected" : ""
                      } ${opt.disabled ? "disabled" : ""}`}
                      onClick={() => !opt.disabled && handleSelect(opt)}
                    >
                      <span className="option-label">
                        {opt[optionLabelKey]}
                      </span>
                      <span
                        className={`radio-circle ${
                          isSelected ? "active" : ""
                        }`}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomSelect;
